import type { TUseDocumentResult, TVersion } from '@modules/documents/types';
import type { AccessType, TPermissions } from '@modules/access/types';
import type { TResolvedItemsMap } from '@modules/items/types/items.types';
import type { TBaseStats } from '@shared/types/stats.types';

export type TChangeTemplateDraftDocumentRef = {
  document: string;
  versionId?: string;
};

export type TChangeTemplateVersionDocumentRef = {
  document: string;
  versionId: string;
};

type TBaseChangeTemplateAssessment<TDocumentRef> = {
  _id: string;
  // The title line of its document at the shown version, which only the document stores.
  title: string;
  required: boolean;
} & TDocumentRef;

export type TChangeTemplateDraftAssessment = TBaseChangeTemplateAssessment<TChangeTemplateDraftDocumentRef>;
// Also the date and authors of the document version that it pins.
export type TChangeTemplateVersionAssessment = TBaseChangeTemplateAssessment<TChangeTemplateVersionDocumentRef> &
  Pick<TVersion, 'completedDate' | 'authors'>;
export type TChangeTemplateAssessment = TChangeTemplateDraftAssessment | TChangeTemplateVersionAssessment;

export type TChangeTemplateDraft = {
  createdDate: string;
  // Its documents' current authors.
  authors: string[];
  request: TChangeTemplateDraftDocumentRef;
  assessments: TChangeTemplateDraftAssessment[];
  description?: string;
};

export type TChangeTemplateVersion = {
  id: string;
  createdDate: string;
  completedDate: string;
  authors: string[];
  note: string;
  request: TChangeTemplateVersionDocumentRef;
  assessments: TChangeTemplateVersionAssessment[];
  description?: string;
};

export type TChangeTemplate = {
  _id: string;
  title: string;
  createdByUser: string;
  stats: TBaseStats;
};

export type TChangeTemplateListItem = TChangeTemplate & {
  hasDraft: boolean;
  assessmentCount: number;
  accessRight: AccessType;
  latestVersionId?: string;
  authors?: string[];
};

export type TChangeTemplateList = {
  items: TChangeTemplateListItem[];
  total: number;
};

export type TChangeTemplateListParams = {
  page: number;
  pageSize: number;
};

export type TChangeTemplateDetails = {
  changeTemplate: TChangeTemplate & {
    versions: TChangeTemplateVersion[];
    draft?: TChangeTemplateDraft;
  };
  permissions: Pick<TPermissions, 'accessRight'>;
  resolvedItems: TResolvedItemsMap;
};

// Each section is a child route of the Change Template page.
export enum EChangeTemplateSection {
  GENERAL = 'general',
  REQUEST = 'request',
  ASSESSMENTS = 'assessments',
}

export type TChangeTemplateOutletContext = {
  details: TChangeTemplateDetails;
  selected: TChangeTemplateDraft | TChangeTemplateVersion;
  canEdit: boolean;
  editLockedReason: string;
  // With edit permission, in the current draft and in template edit mode.
  canChange: boolean;
  assessmentsDisabledReason: string;
  documentError: Error | null;
  // The shown document, once loaded.
  document?: TUseDocumentResult;
  templateVersion?: string;
};

export type TChangeTemplateFormValues = Pick<TChangeTemplate, 'title'> & {
  description: string;
};

export type TCreateChangeTemplateInput = {
  title: string;
  description?: string | null;
};

export type TChangeTemplateAssessmentFormValues = Pick<TChangeTemplateDraftAssessment, 'title' | 'required'>;

// The title starts the title line of the assessment's document, which then stores and edits it.
export type TCreateChangeTemplateAssessmentInput = {
  title: string;
  required?: boolean;
};

export type TUpdateChangeTemplateAssessmentInput = Pick<TChangeTemplateAssessmentFormValues, 'required'>;

// The document open in an editor completes with that editor's lock, like a Document's Complete while editing.
export type TCompleteChangeTemplateInput = {
  note: string;
  editingDocumentId?: string;
};

export const isTChangeTemplateVersion = (
  value: TChangeTemplateDraft | TChangeTemplateVersion
): value is TChangeTemplateVersion => 'id' in value;
